import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';

import { cn } from '@/lib/cn';

export interface RoleTagOption {
  id: number;
  label: string;
}

export interface RoleTagValue {
  id: number;
  role?: string | null;
}

interface RoleTagsComboboxProps {
  selected: RoleTagValue[];
  options: RoleTagOption[];
  /** Roles que ofrecemos en el dropdown del chip. El primer item se usa como default. */
  roles: { value: string; label: string }[];
  onChange: (next: RoleTagValue[]) => void;
  placeholder?: string;
  className?: string;
  emptyMessage?: string;
}

/**
 * Como TagsCombobox pero cada chip lleva un selector de rol al lado del nombre.
 *
 * - Para agregar, escribís en el input → dropdown de opciones disponibles.
 * - Para cambiar el rol, click sobre el rol del chip → dropdown con la lista.
 * - Para quitar, X en el chip.
 *
 * El rol se persiste en el pivot (`enologist_wine.role`, `producer_wine.role`).
 */
export function RoleTagsCombobox({
  selected,
  options,
  roles,
  onChange,
  placeholder = 'Buscar…',
  className,
  emptyMessage = 'Sin resultados',
}: RoleTagsComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedMap = useMemo(() => {
    const m = new Map<number, string | null | undefined>();
    selected.forEach((s) => m.set(s.id, s.role));
    return m;
  }, [selected]);

  // Filtrar opciones disponibles + ordenar alfabéticamente.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const available = options
      .filter((o) => !selectedMap.has(o.id))
      .slice()
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
    if (!q) return available.slice(0, 200);
    return available.filter((o) => o.label.toLowerCase().includes(q)).slice(0, 200);
  }, [options, query, selectedMap]);

  // Chips ordenados por nombre, conservando rol.
  type Tag = { id: number; label: string; role?: string | null };
  const tags = useMemo<Tag[]>(() => {
    const list: Tag[] = [];
    for (const s of selected) {
      const opt = options.find((o) => o.id === s.id);
      if (opt) list.push({ id: s.id, label: opt.label, role: s.role });
    }
    return list.sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
  }, [selected, options]);

  const add = (id: number) => {
    if (selectedMap.has(id)) return;
    onChange([...selected, { id, role: roles[0]?.value ?? null }]);
    setQuery('');
  };

  const remove = (id: number) => {
    onChange(selected.filter((x) => x.id !== id));
  };

  const changeRole = (id: number, role: string | null) => {
    onChange(selected.map((x) => (x.id === id ? { ...x, role } : x)));
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div
        className="input flex flex-wrap gap-1.5 cursor-text min-h-[38px] py-1.5"
        onClick={() => setOpen(true)}
      >
        {tags.map((t) => (
          <span
            key={t.id}
            className="inline-flex items-center gap-1 text-xs pl-2 pr-1 py-0.5 rounded-full bg-notion-100 text-notion-800"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="font-medium">{t.label}</span>
            <select
              className="bg-transparent border-none text-[11px] text-notion-600 focus:outline-none cursor-pointer pr-0"
              value={t.role ?? ''}
              onChange={(e) => changeRole(t.id, e.target.value || null)}
              title="Cambiar rol"
            >
              {roles.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="text-notion-500 hover:text-notion-800"
              onClick={(e) => {
                e.stopPropagation();
                remove(t.id);
              }}
              aria-label={`Quitar ${t.label}`}
            >
              <X size={11} />
            </button>
          </span>
        ))}
        <input
          type="text"
          className="flex-1 min-w-[120px] outline-none bg-transparent text-sm"
          placeholder={tags.length === 0 ? placeholder : ''}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
        />
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-notion-200 bg-white shadow-sm">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-notion-500">{emptyMessage}</div>
          ) : (
            filtered.map((o) => (
              <button
                key={o.id}
                type="button"
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-notion-100 inline-flex items-center gap-2"
                onClick={() => add(o.id)}
              >
                <Plus size={12} className="text-notion-400" />
                {o.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
